import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  X,
  Send,
  Sparkles,
  Phone,
  Bot,
  User,
  ChevronDown,
  Clock,
  Loader2,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  ChatMessage,
  getOrCreateChatSession,
  loadChatMessages,
  sendChatMessage,
  getStoredPhoneNumber,
  storePhoneNumber,
  triggerN8nWorkflow,
} from '../helpers/chatHelpers';
import { VideoRecommendationCard, RecommendedVideo } from './VideoRecommendationCard';

interface FloatingChatWidgetProps {
  currentUserId?: string | null;
  onNavigateToVideo?: (contentId: string, videoId: string) => void;
}

// Formata o texto em Markdown (**negrito**, listas -) e remove links brutos do YouTube
function renderFormattedContent(text: string) {
  if (!text) return null;

  // 1. Remove qualquer link bruto do YouTube tipo [Texto](https://youtube...) ou URLs soltas do YouTube
  let cleanText = text
    .replace(/\[([^\]]+)\]\((https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)[^\)]+)\)/gi, '$1')
    .replace(/(https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/[^\s]+)/gi, '');

  const lines = cleanText.split('\n');

  return (
    <div className="space-y-1.5">
      {lines.map((line, lineIdx) => {
        let trimmed = line.trim();
        if (!trimmed) return <div key={lineIdx} className="h-0.5" />;

        const isListItem = trimmed.startsWith('- ') || trimmed.startsWith('* ');
        if (isListItem) {
          trimmed = trimmed.substring(2).trim();
        }

        // Processa negrito **texto**
        const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);

        const renderedLine = parts.map((part, partIdx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <strong key={partIdx} className="font-bold text-white">
                {part.slice(2, -2)}
              </strong>
            );
          }
          return part;
        });

        if (isListItem) {
          return (
            <div key={lineIdx} className="flex items-start space-x-2 my-0.5 ml-1">
              <span className="text-[#0FFF55] font-bold select-none">•</span>
              <span className="flex-1">{renderedLine}</span>
            </div>
          );
        }

        return <p key={lineIdx}>{renderedLine}</p>;
      })}
    </div>
  );
}

export const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({
  currentUserId,
  onNavigateToVideo,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [phoneNumber, setPhoneNumber] = useState<string>(getStoredPhoneNumber() || '');
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [tempPhoneInput, setTempPhoneInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Inicializar Sessão de Chat
  useEffect(() => {
    async function initSession() {
      const id = await getOrCreateChatSession(currentUserId, phoneNumber);
      if (id) {
        setSessionId(id);
        const history = await loadChatMessages(id);
        setMessages(history);

        // Se não tiver histórico e for o primeiro acesso, adicionar mensagem de boas-vindas
        if (history.length === 0) {
          const welcomeMsg: ChatMessage = {
            id: 'welcome_' + Date.now(),
            session_id: id,
            sender: 'assistant',
            content:
              'Olá! Sou o seu Agente de IA do MasterClub. 🚀\nTenho memorizado todas as transcrições das aulas e conteúdos da nossa plataforma. Como posso te ajudar hoje?',
            created_at: new Date().toISOString(),
          };
          setMessages([welcomeMsg]);
        }
      }
    }
    initSession();
  }, [currentUserId, phoneNumber]);

  // Supabase Realtime Subscription para novas mensagens do Agente (enviadas via n8n/Serverless)
  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`chat_messages:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const newMsg = payload.new as ChatMessage;

          // Ignora mensagens do próprio usuário no Realtime (elas já são inseridas localmente de forma otimista)
          if (newMsg.sender === 'user') return;

          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });

          if (newMsg.sender === 'assistant') {
            setIsProcessing(false);
            if (!isOpen) setHasUnread(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, isOpen]);

// Formata o horário de envio (ex: 14:05)
function formatMessageTime(dateString?: string): string {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

  // Auto-scroll para o final das mensagens ao enviar ou receber
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isProcessing]);

  // Auto-scroll instantâneo para o final quando o chat é ABERTO
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 80);
    }
  }, [isOpen]);

  // Salvar telefone informado
  const handleSavePhone = (e: React.FormEvent) => {
    e.preventDefault();
    if (tempPhoneInput.trim()) {
      storePhoneNumber(tempPhoneInput.trim());
      setPhoneNumber(tempPhoneInput.trim());
      setShowPhonePrompt(false);
    }
  };

  // Enviar Mensagem do Usuário
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputMessage.trim() || !sessionId) return;

    const text = inputMessage.trim();
    setInputMessage('');

    // Mensagem temporária local (otimista)
    const tempId = 'temp_' + Date.now();
    const tempMsg: ChatMessage = {
      id: tempId,
      session_id: sessionId,
      sender: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, tempMsg]);
    setIsProcessing(true);

    // 1. Grava a mensagem no Supabase
    const userMsg = await sendChatMessage(sessionId, 'user', text);
    if (userMsg) {
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? userMsg : m))
      );
    }

    // 2. Aciona a webhook do n8n
    triggerN8nWorkflow({
      sessionId,
      message: text,
      userId: currentUserId,
      phoneNumber,
    });
  };

  return (
    <>
      {/* Botão Flutuante (Ícone Verde no Canto Inferior Direito) */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => {
            setIsOpen(!isOpen);
            setHasUnread(false);
          }}
          className="relative group bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] p-4 rounded-full shadow-xl shadow-[#0FFF55]/30 hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center border border-[#0FFF55]/50"
          aria-label="Abrir Assistente de IA"
        >
          {isOpen ? (
            <X size={26} className="text-[#121316] font-bold" />
          ) : (
            <MessageSquare size={26} className="text-[#121316] fill-[#121316]" />
          )}

          {/* Anel de Pulso Animado */}
          {!isOpen && (
            <span className="absolute -inset-1 rounded-full bg-[#0FFF55]/40 animate-ping pointer-events-none" />
          )}

          {/* Badge de Não Lida */}
          {hasUnread && !isOpen && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#121316]">
              1
            </span>
          )}
        </button>
      </div>

      {/* Modal / Janela do Chatbot */}
      {isOpen && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[92vw] sm:w-[420px] h-[580px] max-h-[80vh] bg-[#121316]/95 backdrop-blur-xl border border-[#0FFF55]/30 rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-slide-in-right">
          {/* Header */}
          <div className="p-4 bg-[#1a1d21]/90 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#0FFF55] to-[#0dd149] flex items-center justify-center text-[#121316] shadow-md shadow-[#0FFF55]/20">
                  <Bot size={22} className="stroke-[2.5]" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#0FFF55] border-2 border-[#121316] rounded-full" />
              </div>
              <div>
                <div className="flex items-center space-x-1.5">
                  <h3 className="text-sm font-black text-white">MasterClub AI</h3>
                  <span className="flex items-center space-x-1 bg-[#0FFF55]/10 border border-[#0FFF55]/30 text-[#0FFF55] text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0FFF55] animate-pulse" />
                    <span>ONLINE</span>
                  </span>
                </div>
                <p className="text-[11px] text-gray-400">Tutor e Recomendador de Aulas</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              {!currentUserId && (
                <button
                  onClick={() => setShowPhonePrompt(true)}
                  className="p-2 text-gray-400 hover:text-[#0FFF55] hover:bg-gray-800/60 rounded-xl transition-colors"
                  title="Identificar com telefone"
                >
                  <Phone size={18} />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800/60 rounded-xl transition-colors"
              >
                <ChevronDown size={20} />
              </button>
            </div>
          </div>

          {/* Modal de Identificação por Telefone (para usuários deslogados) */}
          {showPhonePrompt && (
            <div className="p-4 bg-[#1a1d21] border-b border-[#0FFF55]/20 animate-fade-in">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-[#0FFF55] flex items-center gap-1">
                  <Phone size={14} /> Identificação por Telefone
                </span>
                <button
                  onClick={() => setShowPhonePrompt(false)}
                  className="text-gray-500 hover:text-white"
                >
                  <X size={14} />
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mb-3">
                Digite seu número para salvar seu histórico de dúvidas nas aulas.
              </p>
              <form onSubmit={handleSavePhone} className="flex gap-2">
                <input
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={tempPhoneInput}
                  onChange={(e) => setTempPhoneInput(e.target.value)}
                  className="flex-1 bg-[#121316] text-white text-xs px-3 py-2 rounded-xl border border-gray-700 focus:border-[#0FFF55] outline-none"
                />
                <button
                  type="submit"
                  className="bg-[#0FFF55] text-[#121316] text-xs font-bold px-3 py-2 rounded-xl hover:bg-[#0dd149] transition-colors"
                >
                  Salvar
                </button>
              </form>
            </div>
          )}

          {/* Área de Mensagens */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => {
              const isUser = msg.sender === 'user';
              const recommendedVideos: RecommendedVideo[] =
                msg.metadata?.recommended_videos || [];

              return (
                <div
                  key={msg.id}
                  className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl p-3.5 text-xs sm:text-sm leading-relaxed ${
                      isUser
                        ? 'bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-medium rounded-br-none shadow-md'
                        : 'bg-[#1a1d21] text-gray-200 border border-gray-800 rounded-bl-none'
                    }`}
                  >
                    {!isUser && (
                      <div className="flex items-center space-x-1 mb-1 text-[10px] text-[#0FFF55] font-bold">
                        <Sparkles size={12} />
                        <span>Agente MasterClub</span>
                      </div>
                    )}

                    <div>{renderFormattedContent(msg.content)}</div>

                    {/* Vídeos Recomendados (se houver) */}
                    {recommendedVideos.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-gray-800/80">
                        {recommendedVideos.map((vid, idx) => (
                          <VideoRecommendationCard
                            key={idx}
                            video={vid}
                            onNavigateToVideo={(cId, vId) => {
                              setIsOpen(false);
                              onNavigateToVideo?.(cId, vId);
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {/* Horário de envio da mensagem */}
                    <div
                      className={`text-[9px] mt-1 flex items-center justify-end ${
                        isUser ? 'text-[#121316]/75 font-bold' : 'text-gray-500'
                      }`}
                    >
                      <span>{formatMessageTime(msg.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Status de Carregamento / Digitação da IA (Três pontinhos animados) */}
            {isProcessing && (
              <div className="flex justify-start animate-fade-in">
                <div className="bg-[#1a1d21] text-gray-300 border border-[#0FFF55]/30 rounded-2xl rounded-bl-none px-4 py-3 flex items-center space-x-1.5 shadow-md">
                  <span className="w-2 h-2 bg-[#0FFF55] rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-2 h-2 bg-[#0FFF55] rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-2 h-2 bg-[#0FFF55] rounded-full animate-bounce" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Form / Input de Envio */}
          <form
            onSubmit={handleSendMessage}
            className="p-3 bg-[#1a1d21] border-t border-gray-800 flex items-center space-x-2"
          >
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Tire dúvidas das aulas ou peça sugestões..."
              className="flex-1 bg-[#121316] text-white text-xs sm:text-sm px-4 py-3 rounded-xl border border-gray-700 focus:border-[#0FFF55] outline-none transition-colors placeholder:text-gray-500"
              disabled={isProcessing}
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || isProcessing}
              className="bg-[#0FFF55] hover:bg-[#0dd149] disabled:opacity-40 text-[#121316] p-3 rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 shadow-md shadow-[#0FFF55]/20"
            >
              <Send size={18} className="fill-[#121316]" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
